import { apiClient } from '@api/client';
import { nonNull } from '@helpers/non-null';
import { ErrorPage404, ErrorPage500 } from '@pages/ErrorPage';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Select, Typography } from 'antd';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import './MondayInstall.less';
import { ReactComponent as LockSvg } from '@assets/icons/lock.svg';
import { isAxiosError } from 'axios';

const { Title } = Typography;

interface MondayOAuthInstallProps {
  loader: ReactElement;
}

export default function MondayOAuthInstall({
  loader
}: MondayOAuthInstallProps) {
  const [searchParams] = useSearchParams();
  const mondayClient = searchParams.get('client');
  const mondayToken = searchParams.get('token');
  const mutation = useMutation({
    mutationFn: async (organizationId: string) => {
      const {
        data: { url, handle }
      } = await apiClient.monday.mondayControllerInstall({
        client: mondayClient ?? undefined,
        token: nonNull(mondayToken),
        organizationId
      });
      sessionStorage.setItem('monday-oauth-handle', handle);
      window.location.href = url;
    }
  });
  const { mutate } = mutation;

  const [selectedOrganizationId, setSelectedOrganizationId] =
    useState<string>('');
  const organizationsQuery = useQuery({
    queryKey: ['organizations'],
    queryFn: async () => {
      const {
        data: { list }
      } =
        await apiClient.organization.organizationControllerListUserOrganizations();
      return list;
    }
  });
  const organizations = useMemo(
    () => organizationsQuery.data || [],
    [organizationsQuery.data]
  );

  const defaultOrgId =
    organizations.length === 1 ? organizations[0].id : undefined;
  const isLoading =
    organizationsQuery.isLoading || mutation.isPending || mutation.isSuccess;
  const isError = organizationsQuery.isError || mutation.isError;

  useEffect(() => {
    if (defaultOrgId) mutate(defaultOrgId);
  }, [defaultOrgId, mutate]);

  if (!mondayToken) {
    return <ErrorPage404 />;
  }

  if (isError) {
    if (
      isAxiosError(mutation.error) &&
      mutation.error.response?.data.errorCode ===
        'organization_permission_denied'
    ) {
      return (
        <div className="monday-install">
          <div className="monday-install__content">
            <div className="monday-install__lock-icon">
              <LockSvg />
            </div>
            <Title level={2} className="monday-install__title">
              You don&apos;t have permissions to install integrations
            </Title>
            <Title level={5} className="monday-install__description">
              Contact your organization admin to install the Monday integration
            </Title>
          </div>
        </div>
      );
    }

    if (
      isAxiosError(mutation.error) &&
      mutation.error.response?.data.errorCode ===
        'organization_already_has_monday_integration'
    ) {
      return (
        <div className="monday-install">
          <div className="monday-install__content">
            <Title level={2} className="monday-install__title">
              This organization already has a Monday integration installed
            </Title>
            <Title level={5} className="monday-install__description">
              Contact{' '}
              <a href="mailto:support@streamwork.com">support@streamwork.com</a>{' '}
              if you believe this is a mistake
            </Title>
          </div>
        </div>
      );
    }

    return <ErrorPage500 />;
  }

  if (isLoading) {
    return loader;
  }

  return (
    <div className="monday-install">
      <div className="monday-install__content">
        <Title level={2} className="monday-install__title">
          Select Organization
        </Title>
        <Title level={5} className="monday-install__description">
          Choose an organization to connect with Monday account
        </Title>
        <Select
          className="monday-install__select"
          size="large"
          loading={mutation.isPending}
          placeholder="Select an organization..."
          onChange={setSelectedOrganizationId}
          options={organizations.map((org) => ({
            label: org.name,
            value: org.id
          }))}
        />
        <Button
          className="monday-install__button"
          type="primary"
          onClick={() => mutate(selectedOrganizationId)}
          disabled={!selectedOrganizationId}
        >
          Next
        </Button>
      </div>
    </div>
  );
}
